import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { $sessionVersion, $authChanging, getSessionVersion } from "../store/user_info.ts";
import { createRequestScope, isRefreshCancelled } from "../utils/refresh_resource.ts";

export const PAGE_REFRESH_EVENT = "farpov:page-refresh";
export type RefreshReason = "enter" | "resume" | "online" | "manual" | "mutation" | "same-menu";
export const canonicalPage = (path: string) => path === "/" ? "/overview" : path.split("?")[0];

export function usePageRefresh(options: {
    pageKey: string;
    enabled?: boolean;
    refresh: (reason: RefreshReason) => Promise<unknown>;
    canAutoRefresh?: boolean;
}) {
    const { pageKey } = options;
    const authChanging = useStore($authChanging);
    const enabled = options.enabled !== false && !authChanging;
    const session = useStore($sessionVersion);
    const latest = useRef(options);
    latest.current = { ...options, enabled };
    const [refreshing, setRefreshing] = useState(false);
    const [pendingRefresh, setPendingRefresh] = useState(false);
    const [error, setError] = useState("");
    const life = useRef(0);
    const running = useRef<Promise<void> | undefined>();
    const lastResume = useRef(-Infinity);

    const run = useCallback((reason: RefreshReason): Promise<void> => {
        if (latest.current.enabled === false) return Promise.resolve();
        if (reason !== "manual" && reason !== "mutation" && reason !== "enter" &&
            latest.current.canAutoRefresh === false) {
            setPendingRefresh(true);
            return Promise.resolve();
        }
        if (running.current) return running.current;
        const generation = life.current;
        setRefreshing(true);
        setError("");
        setPendingRefresh(false);
        const action = latest.current.refresh;
        const promise = Promise.resolve().then(() => {
            if (life.current === generation) return action(reason);
        }).then(() => {})
            .catch(err => {
                if (life.current === generation && !isRefreshCancelled(err)) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            }).finally(() => {
                if (life.current === generation) {
                    setRefreshing(false);
                    running.current = undefined;
                }
            });
        running.current = promise;
        return promise;
    }, []);

    useEffect(() => {
        life.current++;
        running.current = undefined;
        setRefreshing(false);
        setPendingRefresh(false);
        setError("");
        if (!enabled) return;
        const visible = () => document.visibilityState !== "hidden";
        const resume = (reason: "resume" | "online" = "resume") => {
            if (!visible()) return;
            const now = Date.now();
            if (now - lastResume.current < 1000) return;
            lastResume.current = now;
            void run(reason);
        };
        const onVisible = () => resume();
        const onOnline = () => resume("online");
        const onPageShow = (event: PageTransitionEvent) => { if (event.persisted) resume(); };
        const onSameMenu = () => {
            if (visible()) void run("same-menu");
        };
        lastResume.current = visible() ? Date.now() : -Infinity;
        if (visible()) void run("enter");
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        window.addEventListener("online", onOnline);
        window.addEventListener("pageshow", onPageShow);
        window.addEventListener(PAGE_REFRESH_EVENT, onSameMenu);
        return () => {
            life.current++;
            running.current = undefined;
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
            window.removeEventListener("online", onOnline);
            window.removeEventListener("pageshow", onPageShow);
            window.removeEventListener(PAGE_REFRESH_EVENT, onSameMenu);
        };
    }, [pageKey, enabled, session, run]);

    return { refreshNow: useCallback(() => run("manual"), [run]), refreshing, pendingRefresh, error };
}

export function useRequestScope(key: string) {
    const session = useStore($sessionVersion);
    const scope = useMemo(() => createRequestScope(getSessionVersion), [key, session]);
    useEffect(() => () => scope.dispose(), [scope]);
    return scope;
}
